import { InvoiceTransaction } from '@/api/useFreight/types/invoice'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import React, { useCallback, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

interface FilterContext {
  activeStatus?: string
  costRevenue?: 'All' | 'Cost' | 'Revenue'
  dateRange?: [Date | undefined, Date | undefined]
}

export function InvoiceNavigationHeader({
  setSelectedOrder,
  isRebillMode = false,
}: {
  setSelectedOrder: React.Dispatch<React.SetStateAction<InvoiceTransaction | null>>
  isRebillMode?: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { displayedRows = [], currentIndex = -1, filterContext = {} as FilterContext } = location.state || {}

  // Determine if we can navigate to prev/next
  const isFirstInvoice = currentIndex <= 0
  const isLastInvoice = currentIndex >= displayedRows.length - 1

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (isFirstInvoice || isRebillMode) return
    const prevId = displayedRows[currentIndex - 1].id
    setSelectedOrder(null)
    navigate(`/freight/invoices/${prevId}`, {
      state: {
        displayedRows,
        currentIndex: currentIndex - 1,
        filterContext,
      },
    })
  }, [displayedRows, currentIndex, isFirstInvoice, isRebillMode, navigate, filterContext, setSelectedOrder])

  const goToNext = useCallback(() => {
    if (isLastInvoice || isRebillMode) return
    const nextId = displayedRows[currentIndex + 1].id
    setSelectedOrder(null)
    navigate(`/freight/invoices/${nextId}`, {
      state: {
        displayedRows,
        currentIndex: currentIndex + 1,
        filterContext,
      },
    })
  }, [displayedRows, currentIndex, isLastInvoice, isRebillMode, navigate, filterContext, setSelectedOrder])

  const backToList = () => {
    navigate('/freight/invoices', {
      state: {
        activeStatus: filterContext?.activeStatus,
        costRevenue: filterContext?.costRevenue,
        dateRange: filterContext?.dateRange,
      },
    })
  }

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle navigation if we have displayedRows
      if (!displayedRows.length) return

      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [displayedRows.length, goToNext, goToPrevious])

  return (
    <Horizontal
      verticalCenter
      justifyContent='space-between'
      className='px-4 py-2'
      bg='elevated'
      border='all'
      borderRadius='6px 6px 0 0'
    >
      <Horizontal verticalCenter gap={8} onClick={backToList} style={{ cursor: 'pointer' }}>
        <ArrowLeftOutlined style={{ fontSize: 16 }} />
        <Texto category='h5' weight='bold'>
          Back to Invoices
        </Texto>
      </Horizontal>
      {displayedRows.length > 0 && !isRebillMode && (
        <Horizontal verticalCenter gap={16}>
          <Texto category='h4' weight='bold'>
            Invoice Navigation
          </Texto>
          <Horizontal verticalCenter gap={8}>
            <Texto category='h5' style={{ minWidth: '80px', textAlign: 'center' }}>
              {currentIndex + 1} of {displayedRows.length}
            </Texto>
            <MaterialIcon
              icon='chevron_left'
              style={{
                fontSize: 24,
                cursor: isFirstInvoice ? 'not-allowed' : 'pointer',
                opacity: isFirstInvoice ? 0.5 : 1,
              }}
              onClick={goToPrevious}
            />
            <MaterialIcon
              icon='chevron_right'
              style={{
                fontSize: 24,
                cursor: isLastInvoice ? 'not-allowed' : 'pointer',
                opacity: isLastInvoice ? 0.5 : 1,
              }}
              onClick={goToNext}
            />
          </Horizontal>
        </Horizontal>
      )}
    </Horizontal>
  )
}
